import { useState } from 'react';
import { Building, ChevronDown, Loading2 } from 'assets/icons';
import CallListBanner from 'components/prospection/CallListBanner';
import CallListStates from 'components/prospection/CallListStates';
import CallRow, { sublineOf } from 'components/prospection/CallRow';
import useText from 'hooks/useText';
import { dayState, splitLines } from 'pages/prospection/callListItems';
import { LIST_GROUP_HEADER_CLASS } from 'utils/surfaceClasses';

const RESULT_TONES = {
  answered: 'bg-green-600 text-white',
  callback: 'bg-yellow-500 text-white',
  no_answer: 'bg-gray-100 text-gray-600',
  not_interested: 'bg-red-100 text-red-700',
};

const STICKY_HEADER_CLASS = `sticky top-0 z-10 flex items-center justify-between gap-3 ${LIST_GROUP_HEADER_CLASS}`;

// A call made today, greyed, with the tag of its result.
const DoneRow = ({ call, text }) => (
  <div
    data-testid="done-row"
    className="flex h-16 items-center gap-3 border-b border-gray-100 px-4"
  >
    <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-gray-100 text-gray-400">
      <Building wh={16} color="currentColor" />
    </span>
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="truncate text-sm font-medium leading-5 text-gray-400">
        {(call.company && call.company.name) || '-'}
      </span>
      <span className="truncate text-xs leading-4 text-gray-400">
        {sublineOf(text, call.company)}
      </span>
    </span>
    <span
      className={`shrink-0 whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium ${RESULT_TONES[call.result] || RESULT_TONES.no_answer}`}
    >
      {text(`results.${call.result}`)}
    </span>
  </div>
);

/**
 * The left column of the prospection page: the goals banner, then the planned
 * calls, the queue and the calls made today, or the empty and finished
 * states. Presentational: the page owns the data and every action.
 */
const CallList = ({
  day,
  loading,
  error,
  now,
  editing,
  selectedCompanyId,
  openCompanyId,
  queueActions,
  onCall,
  onRemove,
  onOpenGlobal,
  onAddProspect,
}) => {
  const { text } = useText('prospection');
  const [showDone, setShowDone] = useState(true);

  if (loading || (!day && !error)) {
    return (
      <div
        data-testid="call-list-loading"
        className="flex h-full min-h-[240px] items-center justify-center"
      >
        <span className="flex motion-safe:animate-spin">
          <Loading2 wh={48} color="#FF9600" />
        </span>
      </div>
    );
  }
  if (error) {
    return (
      <p role="alert" className="p-4 text-sm leading-5 text-red-600">
        {text('states.loadError', { error })}
      </p>
    );
  }

  const state = dayState(day);
  const { planned, queue } = splitLines(day.pending);
  // Most recent first, whatever order the calls came in.
  const made = [...(day.made || [])].sort((a, b) =>
    a.at < b.at ? 1 : a.at > b.at ? -1 : 0,
  );
  const rowOf = (line, rank) => {
    const companyId = line.company && line.company.id;
    return (
      <CallRow
        key={line.id}
        line={line}
        rank={rank}
        now={now}
        editing={editing}
        selected={!!selectedCompanyId && companyId === selectedCompanyId}
        removeDisabled={!!openCompanyId && companyId === openCompanyId}
        onCall={onCall}
        onRemove={onRemove}
      />
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <CallListBanner day={day} editing={editing} />
      <div className="min-h-0 flex-1 overflow-y-auto">
        {state === 'active' ? (
          <>
            {planned.length > 0 && (
              <section>
                <div className={STICKY_HEADER_CLASS}>
                  <p>{text('blocks.planned', { count: planned.length })}</p>
                </div>
                {planned.map((line) => rowOf(line, null))}
              </section>
            )}
            <section>
              <div className={STICKY_HEADER_CLASS}>
                <p>{text('blocks.queue', { count: queue.length })}</p>
                {queueActions}
              </div>
              {queue.length > 0 ? (
                queue.map((line, index) => rowOf(line, index + 1))
              ) : (
                <p className="px-4 py-4 text-sm leading-5 text-gray-500">
                  {text('blocks.queueEmpty')}
                </p>
              )}
            </section>
            {editing && (
              <button
                type="button"
                onClick={onOpenGlobal}
                className="mx-4 my-4 flex h-12 w-[calc(100%-2rem)] items-center justify-center rounded-lg border border-dashed border-gray-300 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                {text('row.pickGlobal')}
              </button>
            )}
          </>
        ) : (
          <CallListStates
            state={state}
            made={made}
            onOpenGlobal={onOpenGlobal}
            onAddProspect={onAddProspect}
          />
        )}
        {made.length > 0 && (
          <section>
            <button
              type="button"
              aria-expanded={showDone}
              onClick={() => setShowDone((shown) => !shown)}
              className={`${STICKY_HEADER_CLASS} w-full text-left transition-colors hover:text-gray-700`}
            >
              {text('blocks.done')}
              <span
                data-testid="done-chevron"
                className={`flex transition-transform ${showDone ? '' : '-rotate-90'}`}
              >
                <ChevronDown wh={14} color="currentColor" />
              </span>
            </button>
            {showDone &&
              made.map((call) => (
                <DoneRow key={call.id} call={call} text={text} />
              ))}
          </section>
        )}
      </div>
    </div>
  );
};

export default CallList;
